import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import type { BoardRes } from '@/api/gen/_model/boardRes';
import type { CreateBoardReq } from '@/api/gen/_model/createBoardReq';
import { useCreateBoard } from '@/api/gen/board-controller/board-controller.gen';
import { HttpStatusCodeEnum, ZepQuizApiError } from '@/api/httpClient';
import { useUser } from '@/lib/auth';
import {
  getRandomBlankBoardBackground,
  getRandomBoardEmoji,
} from '@/shared/features/board/consts';
import type { BoardTemplate } from '@/shared/features/board/types/template';
import { templateToCreateBoardReq } from '@/shared/features/board/utils/templateToCreateBoardReq';

const EMPTY_GROUP_COUNT = 3;

// 생성 트래킹 메타: 어떤 방식(바로시작/템플릿)으로 어떤 구조의 보드를 만들었나.
type CreateTrackMeta = {
  createType: 'blank' | 'template';
  boardType: BoardTemplate['type'];
  templateId?: string;
};

interface UseBoardCreationOptions {
  /** 생성 성공 직후 실행. 이동·목록갱신·토스트 등 후처리는 전부 여기서 한다. */
  onCreated: (board: BoardRes) => void | Promise<void>;
  /** 어느 화면에서 생성했는지 — board_create_success/fail 의 source prop 으로 남는다. */
  source: 'board_new_page' | 'my_board_modal';
}

export function useBoardCreation({
  onCreated,
  source,
}: UseBoardCreationOptions) {
  const { t } = useTranslation('board');
  const { t: tTemplate } = useTranslation('boardTemplates');
  const router = useRouter();
  const { user } = useUser();
  const createBoard = useCreateBoard();

  const createBoardAndNotify = async (
    payload: CreateBoardReq,
    meta: CreateTrackMeta,
  ) => {
    // KPI 유저타입 분포(TEACHER/STUDENT/EMPTY)와 매핑. 비로그인은 'empty'.
    const role = user ? user.type : 'empty';
    const trackBase = {
      create_type: meta.createType,
      board_type: meta.boardType,
      source,
      role,
      ...(meta.templateId ? { template_id: meta.templateId } : {}),
    };

    // 클라에서 먼저 차단: 비로그인은 서버 왕복 없이 바로 로그인으로 보낸다.
    // (서버 403/401 fallback은 로그인 유저가 만료 등으로 거부당하는 경우의 이중 방어)
    if (!user) {
      void router.push({
        pathname: '/login',
        query: { callbackUrl: router.asPath },
      });
      return;
    }

    let created: BoardRes;

    try {
      created = (await createBoard.mutateAsync({ data: payload })).data;
    } catch (error) {
      // 템플릿 콘텐츠(제목/그룹명/공지)가 서버 한도를 넘으면 여기서 조용히 실패한다.
      // 무증상 삼킴을 막기 위해 최소한 콘솔에 남긴다.
      logger.track('board_create_fail', {
        ...trackBase,
        fail_reason: error instanceof Error ? error.message : 'unknown',
      });
      console.error('[board] createBoardFromTemplate failed', error);
      // 비로그인/미인증이 생성 시도 → 서버가 403/401 → 로그인으로 보낸다.
      // 헤더 로그인과 동일하게 callbackUrl(현재 경로)을 남겨 로그인 후 복귀시킨다.
      if (
        error instanceof ZepQuizApiError &&
        (error.httpStatus === HttpStatusCodeEnum.FORBIDDEN_403 ||
          error.httpStatus === HttpStatusCodeEnum.UNAUTHORIZED_401)
      ) {
        void router.push({
          pathname: '/login',
          query: { callbackUrl: router.asPath },
        });
      }
      return;
    }

    // 완료 계측: 기존 enter_create(진입)를 대체하는 '주간 생성 보드 수' 소스.
    logger.track('board_create_success', {
      board_id: created.id,
      ...trackBase,
    });

    // 후처리는 생성 성공 판정 밖에서 돈다. 여기서 던진 에러를 위 catch 가 받으면
    // 보드가 이미 만들어졌는데도 board_create_fail 을 남기고, 403/401 분기를 타면
    // 로그인 화면으로 튕겨 보낸다.
    try {
      await onCreated(created);
    } catch (error) {
      console.error('[board] onCreated failed', error);
    } finally {
      // 모달은 생성 후에도 같은 훅 인스턴스를 재사용한다. success 상태를 남기면
      // 다시 생성 화면에 들어왔을 때 TemplatePicker가 계속 disabled 상태가 된다.
      createBoard.reset();
    }
  };

  const getDefaultTitle = () =>
    user?.username
      ? t('board.header.title.default', { username: user.username })
      : t('board.header.title.new');

  const buildEmptyGroups = (): NonNullable<CreateBoardReq['groups']> =>
    Array.from({ length: EMPTY_GROUP_COUNT }, (_, i) => ({
      name: t('board.group.defaultName', { number: i + 1 }),
    }));

  const createBlankBoard = async (type: BoardTemplate['type']) =>
    createBoardAndNotify(
      {
        title: getDefaultTitle(),
        emoji: getRandomBoardEmoji(),
        background: getRandomBlankBoardBackground(),
        groups: type === 'GROUP' ? buildEmptyGroups() : [],
        posts: [],
        ...(type === 'TIMELINE' ? { layout: 'TIMELINE' as const } : {}),
      },
      { createType: 'blank', boardType: type },
    );

  const createFromTemplate = async (template: BoardTemplate) =>
    createBoardAndNotify(
      // 공지 그룹 이름은 공지사항 배너가 쓰던 그 키를 그대로 쓴다. 생성 시점 언어로
      // 평문 저장되므로(BoardGroup.name 은 런타임 번역 경로가 없다) 여기서 해석해 넘긴다.
      templateToCreateBoardReq(template, tTemplate, t('board.notice.title')),
      {
        createType: 'template',
        boardType: template.type,
        templateId: template.id,
      },
    );

  return {
    createBlankBoard,
    createFromTemplate,
    // isPending 은 mutation resolve 직후 풀리지만, onCreated 후처리(이동·목록 갱신)가
    // 끝나기 전까지 화면이 살아있다. 그 공백에 다른 템플릿을 눌러 보드가 두 개
    // 만들어지는 것을 막기 위해 isSuccess 까지 포함한다.
    isCreating: createBoard.isPending || createBoard.isSuccess,
  };
}
